import { useState, useEffect, useCallback } from "react";
import { SUBFIELD_OPTIONS, DEGREE_OPTIONS } from "../../components/SubfieldDropdown/SubfieldDropdown";
import { mentorSessionRecommendations, FieldScore } from "../../api";

export interface RecommendedPage{
    pageRoute: string;
    seen: boolean;
    priority: number;
}

export interface RecommendedFilters{
    subFields: string[];
    degrees: string[];
    adjusted: boolean;
}

export interface MentorRecommendations{
    recommendedPages: RecommendedPage[];
    recommendedFilters: RecommendedFilters;
}

export const PATHS_TO_RECOMMEND = [
  "/get-research-experience",
  "/explore-careers",
  "/find-graduate-schools",
  "/talk-to-an-expert",
  "/find-internships",
]

export const MENTOR_RECOMMENDATIONS_STORAGE_KEY = "mentorRecommendations";
export const RECOMMENDATIONS_URL_PARAM = "recommendations";

export const PAGE_FIELD_NAME_TO_PATH = {
    "Research": "/get-research-experience",
    "ExploreCareers": "/explore-careers",
    "GradSchool": "/find-graduate-schools",
    "Experts": "/talk-to-an-expert",
    "Internships": "/find-internships",
}

function processPageRecommendations(fieldScores: FieldScore[]): RecommendedPage[] {
    const topPages = fieldScores
        .sort((a, b) => b.score - a.score)
        .slice(0, 3)
        .filter(p => p.score > 0)

    return topPages.map((page, index) => ({
        pageRoute: PAGE_FIELD_NAME_TO_PATH[page.field as keyof typeof PAGE_FIELD_NAME_TO_PATH],
        seen: false,
        priority: index + 1
    }));
}

function processRecommendedFilters(fieldScores: FieldScore[]): RecommendedFilters {
    const recommendedSubFields: string[] = [];
    const recommendedDegrees: string[] = [];

    // Convert field scores to lowercase for case-insensitive matching
    const fieldNames = fieldScores.map(fs => fs.field.toLowerCase());

    // Check if any SUBFIELD_OPTIONS appear in the field scores
    SUBFIELD_OPTIONS.forEach(subfield => {
        if (fieldNames.some(field => field.includes(subfield.toLowerCase()))) {
            recommendedSubFields.push(subfield);
        }
    });

    // Check if any DEGREE_OPTIONS appear in the field scores
    DEGREE_OPTIONS.forEach(degree => {
        if (fieldNames.some(field => field.includes(degree.toLowerCase()))) {
            recommendedDegrees.push(degree);
        }
    });

    return {
        subFields: recommendedSubFields,
        degrees: recommendedDegrees,
        adjusted: false
    };
}

export function useWithRecommendations() {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>();


    const getRecommendations = useCallback(() => {
        // first check url params for filter recommendations.
        const urlParams = new URLSearchParams(window.location.search);
        const urlRecommendations = urlParams.get(RECOMMENDATIONS_URL_PARAM);
        if (urlRecommendations) {
            try{
                const parsedRecommendations: MentorRecommendations = JSON.parse(urlRecommendations)
                return parsedRecommendations;
            } catch (err) {
                console.error("Error parsing url parameters:", err);
            }
        }
        // if no url params, try to get from local storage
        const stored = localStorage.getItem(MENTOR_RECOMMENDATIONS_STORAGE_KEY);
        if (stored) {
            try {
                const parsed: MentorRecommendations = JSON.parse(stored);
                return parsed;
            } catch (err) {
                console.error("Error parsing stored recommendations:", err);
            }
        }
        return null;
    }, []);

    const updatePageSeen = useCallback((pageRoute: string) => {
        const recommendations = getRecommendations();
        if (!recommendations) return;

        const updatedPages = recommendations.recommendedPages.map(page =>
            page.pageRoute === pageRoute ? { ...page, seen: true } : page
        );

        const updatedRecommendations = {
            ...recommendations,
            recommendedPages: updatedPages
        };
        localStorage.setItem(MENTOR_RECOMMENDATIONS_STORAGE_KEY, JSON.stringify(updatedRecommendations));
    }, [getRecommendations]);

    useEffect(()=>{
        // check if the current route we are at is in the recommendations.recommendedPages
        const pathname = window.location.pathname.replace("/recommenderpage", "");
        const recommendations = getRecommendations();
        if (recommendations) {
            const recommendedPageFound = recommendations.recommendedPages.find(page => page.pageRoute.includes(pathname));
            if (recommendedPageFound) {
                updatePageSeen(recommendedPageFound.pageRoute);
            }
        }
    }, [updatePageSeen, getRecommendations])

    const generateRecommendations = async (
        name: string,
        sessionId: string,
        timestampStart: string,
        timestampEnd?: string
    ) => {
        setLoading(true);
        setError(null);

        try {
            const fieldScores = await mentorSessionRecommendations(
                name,
                sessionId,
                10,
                timestampStart,
                timestampEnd
            );

            if (!fieldScores) {
                throw new Error("Failed to fetch recommendations");
            }

            const newRecommendations: MentorRecommendations = {
                recommendedPages: processPageRecommendations(fieldScores.pageFieldScores),
                recommendedFilters: processRecommendedFilters(fieldScores.subfieldScores)
            };
            localStorage.setItem(MENTOR_RECOMMENDATIONS_STORAGE_KEY, JSON.stringify(newRecommendations));
            return newRecommendations;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Unknown error occurred";
            setError(errorMessage);
            console.error("Error generating recommendations:", err);
            clearRecommendations();
        } finally {
            setLoading(false);
        }
    };

    const clearRecommendations = () => {
        localStorage.removeItem(MENTOR_RECOMMENDATIONS_STORAGE_KEY);
    };

    const notifyFiltersAdjusted = () => {
        const recommendations = getRecommendations();
        if (!recommendations) return;

        const updatedRecommendations = {
            ...recommendations,
            recommendedFilters: {
                ...recommendations.recommendedFilters,
                adjusted: true
            }
        };
        localStorage.setItem(MENTOR_RECOMMENDATIONS_STORAGE_KEY, JSON.stringify(updatedRecommendations));
    };


    return {
        loading,
        error,
        generateRecommendations,
        clearRecommendations,
        updatePageSeen,
        notifyFiltersAdjusted,
        getRecommendations
    };
}